/**
 * Camada 1b · a faixa permanente que diz em que ambiente esta tela roda.
 *
 * POR QUE ELA EXISTE: em homologacao a pessoa apaga, aprova e reenvia sem medo, porque o
 * dado e sintetico e gerado por seed versionado. Em producao os mesmos gestos tem
 * consequencia para gente de verdade. A unica coisa que separa os dois e a pessoa SABER
 * onde esta, num relance, sem conferir a URL e sem lembrar em qual aba abriu o que.
 *
 * QUATRO DECISOES QUE FAZEM A FAIXA CUMPRIR ISSO, e cada uma fecha um jeito de burlar:
 *
 * 1. ELA NAO TEM BOTAO, nao tem estado e nao tem `onClick`. Nao existe fechar, nao existe
 *    "nao mostrar de novo" e nao existe preferencia que a desligue. Aviso dispensavel e
 *    dispensado no primeiro dia, e a partir dali ele nao protege mais ninguem.
 *
 * 2. ELA MORA NO FLUXO, e nao em `position: fixed`. Faixa fixa depende de alguem lembrar
 *    de reservar o espaco dela embaixo, e no dia em que a altura mudar o aviso passa a
 *    cobrir a topbar. Sendo a primeira linha da coluna do chassi, o espaco e reservado pelo
 *    proprio layout: nada abaixo dela pode ficar escondido, porque nada abaixo dela ocupa
 *    o mesmo lugar. Nao existe compensacao para alguem esquecer de ajustar.
 *
 * 3. ELA NAO ROLA PARA FORA DA TELA porque o chassi inteiro e `h-screen overflow-hidden` e
 *    quem rola e o `main` la dentro. A faixa esta fora desse contentor, entao nao existe
 *    posicao de rolagem em que ela deixe de aparecer.
 *
 * 4. ELA GANHA DE TUDO QUE FLUTUA. `z-aviso` e a camada mais alta do sistema, acima do
 *    modal. No celular a gaveta do rail e o painel de ajuda sao `fixed inset-0`, ou seja,
 *    comecam no topo da janela e passariam por cima da faixa. Com `relative z-aviso` ela
 *    continua visivel com a gaveta aberta, que e justamente quando alguem esta navegando.
 *
 * DOIS AMBIENTES NAO DESENHAM NADA, e os motivos NAO se confundem:
 *
 *   `prd` porque a faixa e um aviso de ensaio, e em producao nao ha ensaio nenhum.
 *   Desenha-la ali diria a quem mexe em dado de gente de verdade que aquilo e teste.
 *
 *   `local` por pedido do fundador em 06/09/2026, textual: "NAO COLOCAR ESSA FAIXA EM
 *   AMBIENTE DE DESENVOLVIMENTO". Quem roda na propria maquina subiu o processo com as
 *   proprias maos e nao precisa ser lembrado disso a cada tela; ali a faixa era so uma
 *   tira a menos de area util, num lugar onde ela nao evitava erro nenhum.
 *
 * OS DOIS SILENCIOS EXIGEM QUE ALGUEM TENHA ESCRITO O NOME DO AMBIENTE. Ambiente ausente
 * ou com a grafia errada NAO cai em nenhum deles: cai na faixa vermelha de ambiente nao
 * declarado. E a assimetria que sustenta a peca inteira, porque tela sem faixa passou a
 * significar duas coisas boas (producao ou maquina de quem desenvolve), e adivinhar qual
 * delas a partir de uma variavel vazia e exatamente o que nao pode acontecer.
 *
 * SOBRE O `role="note"`, e por que nao `alert` nem `status`: os dois sao regioes vivas,
 * e regiao viva foi feita para anunciar MUDANCA. A faixa nao muda nunca, entao a unica
 * coisa que um `aria-live` produziria aqui e um anuncio repetido a cada remontagem, que e
 * exatamente o oposto do pedido: quem usa leitor de tela precisa ouvir isto uma vez, ao
 * chegar no topo da pagina, e nao a cada navegacao. `note` e estatico e coloca a faixa na
 * ordem de leitura como o primeiro conteudo da tela, que e onde ela precisa estar.
 */

import { t } from '@complexity/i18n';
import type { Ambiente } from '@complexity/config';

export interface FaixaAmbienteProps {
  /**
   * Em que ambiente esta tela roda.
   *
   * O tipo aceita `undefined` E a propriedade e OBRIGATORIA de proposito: quem monta a
   * faixa precisa escrever que nao sabe o ambiente, em vez de simplesmente omitir. Fosse
   * opcional, esquecer a propriedade e nao ter o valor produziriam a mesma tela, e o
   * esquecimento passaria despercebido, que e o defeito que esta faixa existe para pegar.
   */
  readonly ambiente: Ambiente | undefined;
}

/** O que uma faixa desenha. Os dois primeiros campos sao texto de tela e passam por `t`. */
interface Aparencia {
  /** A palavra que a pessoa le de relance. Chave do dicionario, em portugues. */
  readonly rotulo: string;
  /** A frase curta que diz o que aquele ambiente significa na pratica. */
  readonly nota: string;
  /** Nome da custom property do fundo, ja no formato `var(--cx-*)`. */
  readonly fundo: string;
  /** Nome da custom property do texto, escolhida para ter contraste sobre o fundo acima. */
  readonly frente: string;
}

/**
 * A tabela de aparencia, uma linha por ambiente.
 *
 * `Record<Ambiente, ...>` e proposital: acrescentar um quarto ambiente em AMBIENTES quebra
 * a compilacao AQUI ate alguem decidir o que ele desenha. Ambiente novo sem rotulo nao e
 * ambiente sem faixa, e ambiente sem faixa parece producao.
 *
 * DOIS `null` E NAO UM, e a diferenca entre eles esta no cabecalho deste arquivo. O que os
 * dois tem em comum e o que importa aqui: os dois exigem que alguem tenha ESCRITO o nome
 * do ambiente para serem alcancados. Nenhum e o destino de uma variavel vazia.
 */
const APARENCIA: Readonly<Record<Ambiente, Aparencia | null>> = {
  // A maquina de quem desenvolve nao desenha faixa, por pedido do fundador. Os tokens
  // `--cx-ambiente-local` sairam da folha de estilo na mesma edicao: token vivo para um
  // ambiente que nao desenha e o convite para alguem religar a faixa sem decidir nada.
  local: null,
  hml: {
    // O texto e o do pedido dele em 31/08/2026, item 14, com o separador da casa no
    // lugar do travessao, que e proibido por regra dura.
    rotulo: 'AMBIENTE DE HOMOLOGAÇÃO',
    // INSTRUCAO, e nao constatacao, e a troca e o item inteiro. "nada aqui e real" informa;
    // "nao insira dado real" orienta. Quem le a primeira ainda pode colar um CPF de verdade
    // para testar uma mascara, porque nada ali diz para nao fazer isso.
    nota: 'dados sintéticos. Não insira dado real.',
    // O AZUL E O QUE O FUNDADOR APROVOU em 06/09/2026, no lugar do laranja que estava
    // aqui: "PODE MANTER O AZUL EM HOMOLOGACAO TB". O valor mora no token, e nao nesta
    // linha, porque contraste medido e decisao de folha de estilo.
    fundo: 'var(--cx-ambiente-hml)',
    frente: 'var(--cx-ambiente-hml-contraste)',
  },
  prd: null,
};

/**
 * O que a faixa desenha quando ninguem disse em que ambiente a tela esta.
 *
 * A tentacao seria assumir producao e nao desenhar nada, ou assumir local e desenhar a
 * faixa discreta. As duas escolhem por conta propria no exato momento em que a
 * configuracao esta errada, que e o pior momento possivel para adivinhar. O que a tela faz
 * e dizer que nao sabe, em vermelho, porque nao saber e mais grave que qualquer um dos
 * tres ambientes: ela nao pode afirmar que aquilo ali nao e producao.
 *
 * E ELA PESA MAIS DESDE QUE `local` PAROU DE DESENHAR. Antes, uma tela sem faixa so podia
 * ser producao; agora tambem pode ser a maquina de quem desenvolve. E justamente por isso
 * que a variavel ausente nao pode cair no silencio: sem esta faixa, um erro de digitacao
 * em `VITE_CX_AMBIENTE` produziria uma tela calada e indistinguivel das duas legitimas.
 */
const APARENCIA_INDEFINIDA: Aparencia = {
  rotulo: 'AMBIENTE NÃO DECLARADO',
  nota: 'esta tela não sabe se é produção',
  fundo: 'var(--cx-ambiente-indefinido)',
  frente: 'var(--cx-ambiente-indefinido-contraste)',
};

export function FaixaAmbiente({ ambiente }: FaixaAmbienteProps) {
  const aparencia = ambiente === undefined ? APARENCIA_INDEFINIDA : APARENCIA[ambiente];

  // Producao e local: o chassi sobe sem faixa nenhuma. Sao os dois unicos caminhos que
  // devolvem null, e os dois exigem que o ambiente tenha sido DECLARADO para chegar aqui.
  if (aparencia === null) {
    return null;
  }

  return (
    <div
      role="note"
      // `shrink-0` e o que impede a faixa de ser espremida a zero quando a coluna do chassi
      // fica apertada: sem ele, um flex container em tela baixa pode reduzir a altura dela
      // ate o aviso sumir sem que nada acuse.
      //
      // `text-cx-xs` com `py-cx-1` fecha 24px de altura, que em 390x844 e 2,8% da tela.
      // A faixa precisa ser inconfundivel, e nao grande: grande demais ela come a area util
      // do celular, e ai alguem pede para tirar, que e como aviso morre.
      className="relative z-aviso flex shrink-0 items-center justify-center gap-cx-2 px-cx-3 py-cx-1 text-cx-xs"
      // A cor vem da custom property inteira, e nao de classe do Tailwind, porque estes
      // quatro tokens ainda nao estao no preset compartilhado. Trocar para `bg-ambiente-hml`
      // no dia em que estiverem e edicao de duas linhas aqui.
      style={{ backgroundColor: aparencia.fundo, color: aparencia.frente }}
    >
      {/* O rotulo NAO trunca, e essa e a prioridade da faixa em tela estreita: a palavra
          que diz o ambiente e a unica parte indispensavel. A caixa alta vale para as duas
          faixas que sobraram: as duas avisam, e nenhuma delas e so contexto. */}
      <strong className="shrink-0 font-bold uppercase tracking-wide">{t(aparencia.rotulo)}</strong>

      {/* Separador so visual. Escondido do leitor de tela para nao virar "ponto medio"
          falado no meio da frase. */}
      <span aria-hidden="true">·</span>

      {/* A nota NUNCA some no celular: `hidden md:inline` faria a explicacao nao existir
          justamente onde a recepcao trabalha. E ela QUEBRA em duas linhas em vez de
          truncar, porque as duas notas que restaram sao INSTRUCAO: `truncate` cortaria
          exatamente "Nao insira dado real" e "nao sabe se e producao", que e a unica parte
          acionavel de cada uma. A faixa continuaria bonita tendo deixado de servir para o
          que existe. `min-w-0` e o que permite a quebra dentro do flex. */}
      <span className="min-w-0">{t(aparencia.nota)}</span>
    </div>
  );
}
